import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Download, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const HeadWiseReportDetailView = ({
  approvedRuns = [],
  initialRunId,
  initialMonth = 'ALL',
  initialYear = 'ALL',
}) => {
  const { hasPermission } = useAuth();
  const [runsList, setRunsList] = useState(approvedRuns);
  const [selectedRunId, setSelectedRunId] = useState(
    initialRunId || (approvedRuns && approvedRuns[0] ? approvedRuns[0].id : null)
  );
  const [employees, setEmployees] = useState([]);
  const [runInfo, setRunInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(() => {
    const currentYear = new Date().getFullYear().toString();
    return initialYear && initialYear !== 'ALL' ? initialYear : currentYear;
  });
  const [selectedMonth, setSelectedMonth] = useState('ALL');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [selectedHead, setSelectedHead] = useState('510 (F-4-a)');
  const [headToDepartments, setHeadToDepartments] = useState({});

  const monthsList = [
    { value: 'ALL', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const yearsList = Array.from({ length: Math.max(1, new Date().getFullYear() - 2025 + 1) }, (_, i) => String(2025 + i));

  const printRef = useRef();

  useEffect(() => {
    if (approvedRuns && approvedRuns.length > 0) {
      setRunsList(approvedRuns);
      if (!selectedRunId && approvedRuns[0]) {
        setSelectedRunId(approvedRuns[0].id);
      }
    } else {
      const fetchRuns = async () => {
        try {
          const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            const list = Array.isArray(data) ? data : [];
            setRunsList(list);
            if (list.length > 0 && !selectedRunId) {
              setSelectedRunId(list[0].id);
            }
          }
        } catch (err) {
          console.error('Failed to load fallback runs in HeadWiseReportDetailView:', err);
        }
      };
      fetchRuns();
    }
  }, [approvedRuns]);

  useEffect(() => {
    if (initialRunId) setSelectedRunId(initialRunId);
  }, [initialRunId]);

  const filteredRuns = useMemo(() => {
    return runsList.filter((run) => {
      if (selectedMonth === 'ALL') {
        return Number(run.payroll_year) === Number(selectedYear);
      } else {
        return Number(run.payroll_month) === Number(selectedMonth) && Number(run.payroll_year) === Number(selectedYear);
      }
    });
  }, [runsList, selectedYear, selectedMonth]);

  useEffect(() => {
    if (filteredRuns.length > 0) {
      setSelectedRunId(filteredRuns[0].id);
    } else {
      setSelectedRunId(null);
      setEmployees([]);
    }
  }, [filteredRuns]);

  useEffect(() => {
    let active = true;
    if (selectedRunId) {
      const load = async () => {
        setLoading(true);
        setError('');
        try {
          const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${selectedRunId}/detailed-data`), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          if (!response.ok) {
            throw new Error('Failed to load head-wise payroll data');
          }
          const data = await response.json();
          if (active) {
            setRunInfo(data.payroll_run);
            setHeadToDepartments(data.head_to_departments || {});
            setEmployees(data.employees || []);
          }
        } catch (err) {
          console.error(err);
          if (active) {
            setError(err.message || 'Error loading head-wise detail data');
          }
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };
      load();
    } else {
      setEmployees([]);
    }
    return () => {
      active = false;
    };
  }, [selectedRunId]);

  const getMonthName = (month) => {
    if (!month) return '';
    return new Date(2025, month - 1).toLocaleString('default', { month: 'long' }).toUpperCase();
  };

  // Process data for selected head breakdown
  const reportData = useMemo(() => {
    const targetHead = selectedHead;
    const deptBreakdown = {};

    if (employees && employees.length > 0) {
      employees.forEach((emp) => {
        const head = emp.head_code || 'UNASSIGNED';
        if (head !== targetHead) return;

        const additions = emp.additions || {};
        const basic = Number(additions.basic || 0);
        const da = Number(additions.da || 0);
        const hra = Number(additions.hra || 0);
        const cca = Number(additions.cca || 0);
        const otherAllow =
          Number(additions.tpt || 0) +
          Number(additions.allowance || 0) +
          Number(additions.other_allowance || 0) +
          Number(additions.grade_pay || 0);

        const total = basic + da + hra + cca + otherAllow;
        const deptCode = emp.department_code || emp.sec_code || 'GEN';

        if (!deptBreakdown[deptCode]) {
          deptBreakdown[deptCode] = {
            code: deptCode,
            basic: 0,
            da: 0,
            hra: 0,
            cca: 0,
            other_allowance: 0,
            total: 0
          };
        }
        deptBreakdown[deptCode].basic += basic;
        deptBreakdown[deptCode].da += da;
        deptBreakdown[deptCode].hra += hra;
        deptBreakdown[deptCode].cca += cca;
        deptBreakdown[deptCode].other_allowance += otherAllow;
        deptBreakdown[deptCode].total += total;
      });
    }

    const subBreakdownDepts = [];
    if (targetHead === '510 (F-4-a)') {
      const standard510 = ['WAR', 'BCT', 'GAR', 'consy', 'BNT', 'BST', 'MKT'];
      standard510.forEach((code) => {
        subBreakdownDepts.push({ code, name: code });
      });
    } else {
      const mapped = headToDepartments[targetHead] || [];
      mapped.forEach((code) => {
        subBreakdownDepts.push({ code, name: code });
      });
    }

    // Add any other section code that has actual data for this head
    Object.keys(deptBreakdown).forEach((code) => {
      if (!subBreakdownDepts.some((d) => d.code.toLowerCase() === code.toLowerCase())) {
        subBreakdownDepts.push({ code, name: code });
      }
    });

    // If no sections populated at all, fallback to a default list so it's not empty
    if (subBreakdownDepts.length === 0) {
      const fallbackList = ['WAR', 'BCT', 'GAR', 'consy', 'BNT', 'BST', 'MKT'];
      fallbackList.forEach((code) => {
        subBreakdownDepts.push({ code, name: code });
      });
    }

    const subBreakdown = subBreakdownDepts.map((d) => {
      const matchedKey = Object.keys(deptBreakdown).find(
        (k) => k.toLowerCase() === d.code.toLowerCase()
      );
      return deptBreakdown[matchedKey] || {
        code: d.code,
        basic: 0,
        da: 0,
        hra: 0,
        cca: 0,
        other_allowance: 0,
        total: 0
      };
    });

    const subBreakdownTotal = subBreakdown.reduce(
      (acc, r) => {
        acc.basic += r.basic;
        acc.da += r.da;
        acc.hra += r.hra;
        acc.cca += r.cca;
        acc.other_allowance += r.other_allowance;
        acc.total += r.total;
        return acc;
      },
      { basic: 0, da: 0, hra: 0, cca: 0, other_allowance: 0, total: 0 }
    );

    return { subBreakdown, subBreakdownTotal, head510Code: targetHead };
  }, [employees, selectedHead, headToDepartments]);

  const formatCurrency = (val) => {
    if (!val || val === 0) return '-';
    return '₹' + Number(val).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const handleDownloadPdf = async () => {
    if (!selectedRunId) return;
    setDownloadingPdf(true);
    try {
      await downloadAuthenticatedFile(
        `/api/v1/reports/head-wise-detail-pdf/${selectedRunId}?head_code=${encodeURIComponent(selectedHead)}`,
        `Head_Wise_Report_${selectedHead.replace(/\s+/g, '_')}_${runInfo?.month || 'JUNE'}_${runInfo?.year || 2026}.pdf`
      );
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF report: ' + (err.message || 'Server error'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls Bar - Screen Only */}
      <div className="print:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 w-full">
          <div className="flex flex-wrap items-center gap-3">
            <Calendar className="w-5 h-5 text-blue-600 self-center" />

            {/* Year Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[140px]"
              >
                {yearsList.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Month Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[140px]"
              >
                {monthsList.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Payroll Run Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase">Payroll Run</label>
              <select
                value={selectedRunId || ''}
                onChange={(e) => setSelectedRunId(Number(e.target.value))}
                className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[200px]"
              >
                {filteredRuns.length === 0 ? (
                  <option value="">No runs available</option>
                ) : (
                  filteredRuns.map((r) => (
                    <option key={r.id} value={r.id}>
                      {getMonthName(r.payroll_month)} {r.payroll_year} ({r.status || 'draft'})
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Budget Head Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase">Budget Head</label>
              <select
                value={selectedHead}
                onChange={(e) => setSelectedHead(e.target.value)}
                className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[150px]"
              >
                {[
                  "510 (F-4-a)", "610 (E-4-a)"
                ].map((head) => (
                  <option key={head} value={head}>
                    {head}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {hasPermission('report:export') && (
              <button
                onClick={handleDownloadPdf}
                disabled={!selectedRunId || downloadingPdf}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors ${!selectedRunId || downloadingPdf ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {downloadingPdf ? 'Downloading...' : 'Download PDF'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col justify-center items-center h-64 bg-white rounded-xl border border-gray-200">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
          <p className="text-gray-600 font-medium text-sm">Loading head-wise breakdown report...</p>
        </div>
      ) : (
        /* Report Paper UI matching PDF */
        <div ref={printRef} className="bg-white p-8 rounded-xl border border-gray-300 shadow-md max-w-5xl mx-auto font-sans text-gray-900">
          {/* Document Title */}
          <div className="text-center font-bold text-lg tracking-wider mb-6 text-gray-900 border-b pb-3">
            SALARY FOR {runInfo ? `${getMonthName(runInfo.month)} ${runInfo.year}` : 'JUNE 2026'}
          </div>

          {/* Sub Breakdown Table for Head 510 (F-4-a) */}
          <div>
            <div className="font-bold text-sm mb-3 uppercase">{reportData.head510Code} BREAKDOWN</div>
            <table className="w-full border-collapse border border-black text-xs font-mono">
              <thead>
                <tr className="bg-gray-100 border-b border-black">
                  <th className="border border-black px-2 py-1.5 text-left font-bold">SECTION</th>
                  <th className="border border-black px-2 py-1.5 text-right font-bold">Basic Pay</th>
                  <th className="border border-black px-2 py-1.5 text-right font-bold">DA</th>
                  <th className="border border-black px-2 py-1.5 text-right font-bold">HRA</th>
                  <th className="border border-black px-2 py-1.5 text-right font-bold">CCA</th>
                  <th className="border border-black px-2 py-1.5 text-right font-bold">TPT Allowance & Other Allowance</th>
                  <th className="border border-black px-2 py-1.5 text-right font-bold">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {reportData.subBreakdown.map((row) => (
                  <tr key={row.code} className="hover:bg-gray-50 border-b border-gray-300">
                    <td className="border border-black px-2 py-1 font-bold text-left uppercase">{row.code}</td>
                    <td className="border border-black px-2 py-1 text-right">{formatCurrency(row.basic)}</td>
                    <td className="border border-black px-2 py-1 text-right">{formatCurrency(row.da)}</td>
                    <td className="border border-black px-2 py-1 text-right">{formatCurrency(row.hra)}</td>
                    <td className="border border-black px-2 py-1 text-right">{formatCurrency(row.cca)}</td>
                    <td className="border border-black px-2 py-1 text-right">{formatCurrency(row.other_allowance)}</td>
                    <td className="border border-black px-2 py-1 text-right font-semibold">{formatCurrency(row.total)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-bold border-t-2 border-b-2 border-black">
                  <td className="border border-black px-2 py-1.5 text-left uppercase">SUBTOTAL ({reportData.head510Code})</td>
                  <td className="border border-black px-2 py-1.5 text-right">{formatCurrency(reportData.subBreakdownTotal.basic)}</td>
                  <td className="border border-black px-2 py-1.5 text-right">{formatCurrency(reportData.subBreakdownTotal.da)}</td>
                  <td className="border border-black px-2 py-1.5 text-right">{formatCurrency(reportData.subBreakdownTotal.hra)}</td>
                  <td className="border border-black px-2 py-1.5 text-right">{formatCurrency(reportData.subBreakdownTotal.cca)}</td>
                  <td className="border border-black px-2 py-1.5 text-right">{formatCurrency(reportData.subBreakdownTotal.other_allowance)}</td>
                  <td className="border border-black px-2 py-1.5 text-right font-bold">{formatCurrency(reportData.subBreakdownTotal.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadWiseReportDetailView;
